import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Button, Divider, Text
} from 'components/ui';
import DefaultPaymentCheckbox from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Venmo/DefaultPaymentCheckbox';
import {
    breakpoints, colors, fontSizes, fontWeights, lineHeights, mediaQueries, space
} from 'style/config';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import PayPalCheckout from 'utils/PayPalCheckout';
import { PAY_PAL } from 'constants/PayPal';

const PayPalExpandedView = ({
    paypalHeader = 'Pay with PayPal',
    paypalLegalNotice,
    paypalSaveAndContinue = 'Save and Continue',
    paypalCancel = 'Cancel',
    onSaveAndContinue,
    onCancel,
    isAnonymous,
    onDefaultPaymentMethodSelected
}) => {
    const headerRef = useRef(null);
    // SSR-safe: UA-based initial value matches on server and first client render to avoid hydration mismatch.
    const [isMobile, setIsMobile] = useState(() => typeof Sephora !== 'undefined' && Sephora.isMobile());

    useEffect(() => {
        headerRef.current?.focus();

        FrictionlessCheckoutBindings.setAlternatePaymentExpandedViewAnalytics('paypal');
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return undefined;
        }

        const mobileViewportQuery = window.matchMedia(breakpoints.xsMax);
        const syncIsMobile = () => setIsMobile(mobileViewportQuery.matches);
        syncIsMobile();
        mobileViewportQuery.addEventListener('change', syncIsMobile);

        return () => mobileViewportQuery.removeEventListener('change', syncIsMobile);
    }, []);

    const saveButton = (
        <Button
            variant='primary'
            css={styles.saveButton}
            onClick={onSaveAndContinue}
            data-testid='paypal-save-continue'
            aria-label='Save PayPal and return to payment options'
        >
            {paypalSaveAndContinue}
        </Button>
    );

    const cancelButton = (
        <Button
            variant='secondary'
            css={styles.cancelButton}
            onClick={onCancel}
            data-testid='paypal-cancel'
            aria-label='Cancel PayPal selection and return'
        >
            {paypalCancel}
        </Button>
    );

    return (
        <Box
            role='region'
            aria-labelledby='paypal-expanded-header'
            data-testid='paypal-expanded-view'
        >
            <Text
                as='h3'
                id='paypal-expanded-header'
                ref={headerRef}
                tabIndex={-1}
                css={styles.header}
            >
                {paypalHeader}
            </Text>
            {!isAnonymous && PayPalCheckout.isSetPayPalAsDefaultPaymentEnabled() && (
                <Box css={styles.checkboxContainer}>
                    <DefaultPaymentCheckbox
                        paymentName={PAY_PAL}
                        useSelectedDefaultOnly
                        onToggle={onDefaultPaymentMethodSelected}
                    />
                </Box>
            )}
            {paypalLegalNotice && (
                <Text
                    is='p'
                    css={styles.legalNotice}
                    dangerouslySetInnerHTML={{ __html: paypalLegalNotice }}
                />
            )}
            <Divider css={styles.divider} />
            <Box css={styles.buttonContainer}>
                {isMobile ? (
                    <>
                        {saveButton}
                        {cancelButton}
                    </>
                ) : (
                    <>
                        {cancelButton}
                        {saveButton}
                    </>
                )}
            </Box>
        </Box>
    );
};

const styles = {
    header: {
        color: colors.black,
        fontSize: `${fontSizes.base}px`,
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.base,
        marginTop: space[3],
        marginBottom: space[1]
    },
    checkboxContainer: {
        marginTop: space[2],
        marginBottom: space[2]
    },
    legalNotice: {
        color: colors.black,
        fontSize: `${fontSizes.sm}px`,
        lineHeight: lineHeights.base,
        marginBottom: space[3],
        marginTop: space[2],
        '& a': {
            textDecoration: 'underline',
            color: colors.blue
        }
    },
    divider: {
        marginTop: space[3],
        marginBottom: space[3],
        borderColor: colors.lightGray,
        display: 'none',
        [mediaQueries.sm]: {
            display: 'block'
        }
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[2],
        [mediaQueries.sm]: {
            flexDirection: 'row',
            justifyContent: 'flex-start',
            gap: space[3]
        }
    },
    cancelButton: {
        width: '100%',
        [mediaQueries.sm]: {
            width: 'auto',
            minWidth: '310px'
        }
    },
    saveButton: {
        width: '100%',
        [mediaQueries.sm]: {
            width: 'auto',
            minWidth: '310px'
        }
    }
};

PayPalExpandedView.propTypes = {
    paypalHeader: PropTypes.string,
    paypalLegalNotice: PropTypes.string,
    paypalSaveAndContinue: PropTypes.string,
    paypalCancel: PropTypes.string,
    onSaveAndContinue: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    isAnonymous: PropTypes.bool,
    onDefaultPaymentMethodSelected: PropTypes.func
};

export default wrapFunctionalComponent(PayPalExpandedView, 'PayPalExpandedView');
